import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Globe, ExternalLink, Copy, Check, Shield, Bell, Lock } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import BottomNav from '../components/BottomNav';

export default function Settings() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [copied, setCopied] = React.useState(false);

  const currentDomain = window.location.hostname;

  const copyDNS = (text) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="min-h-screen bg-[#050505] pb-24">
      <header className="px-6 py-6 flex items-center gap-3">
        <button
          data-testid="back-btn"
          onClick={() => navigate('/app/profile')}
          className="w-10 h-10 rounded-full bg-zinc-900 flex items-center justify-center text-zinc-400 hover:text-white"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <h1 className="font-outfit text-2xl font-semibold text-white">Settings</h1>
      </header>

      <main className="px-6 max-w-2xl mx-auto space-y-6">
        {/* Notifications */}
        <section className="p-5 rounded-2xl bg-zinc-900/50 border border-zinc-800">
          <div className="flex items-center gap-2 mb-4">
            <Bell className="w-5 h-5 text-rose-500" />
            <h2 className="font-outfit font-semibold text-white">Notifications</h2>
          </div>
          <p className="text-zinc-400 text-sm mb-4">
            Browser push notifications are {('Notification' in window && Notification.permission === 'granted') ? 'enabled' : 'disabled'}.
          </p>
          <Button
            data-testid="enable-notifications-btn"
            onClick={() => {
              if ('Notification' in window) {
                Notification.requestPermission().then(p => {
                  if (p === 'granted') {
                    new Notification('LocalFlame', { body: 'Notifications enabled!' });
                  }
                });
              }
            }}
            variant="outline"
            className="border-zinc-700 text-zinc-300 hover:bg-zinc-800 rounded-full"
          >
            <Bell className="w-4 h-4 mr-2" />
            Enable Push Notifications
          </Button>
        </section>

        {/* Privacy */}
        <section className="p-5 rounded-2xl bg-zinc-900/50 border border-zinc-800">
          <div className="flex items-center gap-2 mb-4">
            <Lock className="w-5 h-5 text-rose-500" />
            <h2 className="font-outfit font-semibold text-white">Privacy & Safety</h2>
          </div>
          <div className="space-y-3">
            <Button
              data-testid="blocked-users-btn"
              onClick={() => navigate('/app/blocked')}
              variant="outline"
              className="w-full justify-start border-zinc-700 text-zinc-300 hover:bg-zinc-800"
            >
              <Shield className="w-4 h-4 mr-2" />
              Blocked Users
            </Button>
          </div>
        </section>

        {/* Custom Domain (Admin only) */}
        {user?.is_admin && (
          <section className="p-5 rounded-2xl bg-zinc-900/50 border border-zinc-800">
            <div className="flex items-center gap-2 mb-4">
              <Globe className="w-5 h-5 text-rose-500" />
              <h2 className="font-outfit font-semibold text-white">Custom Domain</h2>
            </div>
            
            <div className="mb-4 p-3 rounded-xl bg-zinc-950/50 border border-zinc-800">
              <p className="text-zinc-500 text-xs mb-1">Current Domain</p>
              <p className="text-white font-mono text-sm">{currentDomain}</p>
            </div>

            <div className="space-y-4">
              <div>
                <h3 className="text-white font-medium text-sm mb-2">How to connect your custom domain:</h3>
                <ol className="space-y-3 text-zinc-400 text-sm">
                  <li className="flex gap-2">
                    <span className="text-rose-500 font-bold">1.</span>
                    <div>
                      <p>Purchase a domain (e.g., <span className="text-white">localflame.app</span>) from Namecheap, GoDaddy, or Cloudflare.</p>
                    </div>
                  </li>
                  <li className="flex gap-2">
                    <span className="text-rose-500 font-bold">2.</span>
                    <div>
                      <p>Add a CNAME record pointing to your Emergent deployment:</p>
                      <div className="mt-2 p-3 rounded-lg bg-zinc-900 border border-zinc-800 font-mono text-xs">
                        <div className="flex justify-between items-center">
                          <div>
                            <span className="text-zinc-500">Type:</span> <span className="text-green-400">CNAME</span><br/>
                            <span className="text-zinc-500">Name:</span> <span className="text-white">@</span> or <span className="text-white">www</span><br/>
                            <span className="text-zinc-500">Value:</span> <span className="text-blue-400">{currentDomain}</span>
                          </div>
                          <button
                            data-testid="copy-cname"
                            onClick={() => copyDNS(currentDomain)}
                            className="px-2 py-1 rounded bg-zinc-800 text-zinc-300 text-xs"
                          >
                            {copied ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
                          </button>
                        </div>
                      </div>
                    </div>
                  </li>
                  <li className="flex gap-2">
                    <span className="text-rose-500 font-bold">3.</span>
                    <p>After DNS propagation (up to 24 hours), your custom domain will point to your app.</p>
                  </li>
                  <li className="flex gap-2">
                    <span className="text-rose-500 font-bold">4.</span>
                    <p>For SSL, use Cloudflare as your DNS provider — it provides free SSL automatically.</p>
                  </li>
                </ol>
              </div>

              <div className="p-3 rounded-xl bg-blue-500/10 border border-blue-500/20">
                <p className="text-blue-400 text-sm">
                  <strong>Tip:</strong> Cloudflare Registrar offers domains at cost (no markup) and includes free SSL, CDN, and DDoS protection.
                </p>
              </div>
            </div>
          </section>
        )}
      </main>

      <BottomNav active="profile" />
    </div>
  );
}
